import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CheckCircle2,
  XCircle,
  MinusCircle,
  Camera,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Factory,
  MapPin,
  ClipboardList,
  Upload,
  X,
  QrCode,
  Image as ImageIcon,
  RotateCcw,
  Flag,
  Gauge,
  Clock,
  Package,
  TrendingUp,
  Copy,
  Plug,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { calculerTRS } from '../utils/trsCalcul';
import { TourSummaryExport, CompleteTourExport } from '../components/ExportButtons';

import { API_BASE, BACKEND_URL } from '../utils/apiClient';

const API = API_BASE;

const STEPS = ['site', 'zone', 'checklist', 'audit', 'zone_summary', 'next_zone', 'final_summary', 'success'];

const GembaWalk = () => {
  const { getAuthHeaders, user, isDemoReadonly } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [deployingTemplate, setDeployingTemplate] = useState(false);

  // Selection
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedChecklist, setSelectedChecklist] = useState(null);

  // Multi-zone tour state
  const [completedZones, setCompletedZones] = useState([]); // Array of { zone, checklist, responses, trsData }
  const [currentZoneData, setCurrentZoneData] = useState(null);

  // Audit state
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // TRS (OEE) state for machine zones
  const [trsData, setTrsData] = useState({
    theoretical_cadence: '',
    planned_time: '',
    downtime: '',
    total_production: '',
    conforming_production: '',
    shift: ''
  });
  const [showTrs, setShowTrs] = useState(false);
  const [workShifts, setWorkShifts] = useState(['Jour', 'Soir', 'Nuit']); // Configurable work shifts
  const [isNotPlanned, setIsNotPlanned] = useState(false); // Machine non planifiée
  const [trsObjectiveGlobal, setTrsObjectiveGlobal] = useState(85); // Objectif TRS global

  // Tour rank and shift state
  const [tourRankInfo, setTourRankInfo] = useState(null);
  const [selectedTourRank, setSelectedTourRank] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);

  // Pilote ProdTrack : préremplissage du TRS (facultatif, activé par le super admin)
  const [prodtrackStatus, setProdtrackStatus] = useState({ enabled: false, zones: [] });
  const [prodtrack, setProdtrack] = useState(null); // { loading, available, values, source, warnings, reason, unit }

  // Success state for showing export options after submission
  const [createdTourIds, setCreatedTourIds] = useState([]);
  const [tourSummaryStats, setTourSummaryStats] = useState(null);

  // Get today's date formatted
  const getTodayDate = () => {
    const today = new Date();
    return today.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Check if zone is a machine/production type that needs TRS
  const isMachineZone = (zone) => {
    if (!zone) return false;
    const machineTypes = ['Production', 'Machine'];
    return machineTypes.includes(zone.type);
  };

  // Pilote ProdTrack : la zone est-elle reliée à une ligne ProdTrack ?
  const zoneReliee = (zone) => !!zone && prodtrackStatus.enabled && prodtrackStatus.zones.includes(zone.id);

  const chargerProdtrack = async (zone) => {
    if (!zoneReliee(zone) || !selectedShift) return;
    setProdtrack({ loading: true });
    try {
      const jour = new Date().toISOString().split('T')[0];
      const r = await axios.get(`${API}/integrations/prodtrack/prefill`, {
        params: { zone_id: zone.id, shift: selectedShift, date: jour },
        headers: getAuthHeaders()
      });
      const d = r.data || {};
      if (d.available) {
        const v = d.values || {};
        const txt = (x) => (x === null || x === undefined ? '' : String(x));
        // Seules les valeurs réellement fournies par ProdTrack remplacent la saisie ; jamais de zéro inventé
        setTrsData((prev) => ({
          ...prev,
          planned_time: v.planned_time != null ? txt(v.planned_time) : prev.planned_time,
          downtime: v.downtime != null ? txt(v.downtime) : prev.downtime,
          total_production: v.total_production != null ? txt(v.total_production) : prev.total_production,
          conforming_production: v.conforming_production != null ? txt(v.conforming_production) : prev.conforming_production,
          theoretical_cadence: v.theoretical_cadence != null ? txt(v.theoretical_cadence) : prev.theoretical_cadence,
        }));
        setIsNotPlanned(false);
      }
      setProdtrack({ loading: false, ...d });
    } catch (e) {
      setProdtrack({ loading: false, available: false, reason: 'ProdTrack indisponible : saisie manuelle.' });
    }
  };

  // Récupération automatique à l'ouverture du bloc TRS d'une zone reliée
  useEffect(() => {
    if (step === 4 && isMachineZone(selectedZone) && zoneReliee(selectedZone)) {
      chargerProdtrack(selectedZone);
    } else if (!zoneReliee(selectedZone)) {
      setProdtrack(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedZone?.id, prodtrackStatus.enabled]);

  // Trace jointe à l'entrée TRS (aucune incidence sur le calcul)
  const traceProdtrack = () => (prodtrack?.available ? { source: prodtrack.source, prefilled_values: prodtrack.values } : null);

  // Calculate TRS values in real-time
  const calculateTrs = () => {
    const cadence = parseFloat(trsData.theoretical_cadence) || 0;
    const plannedTime = parseFloat(trsData.planned_time) || 0;
    const downtime = parseFloat(trsData.downtime) || 0;
    const totalProd = parseFloat(trsData.total_production) || 0;
    const conformingProd = parseFloat(trsData.conforming_production) || 0;

    if (plannedTime <= 0 || cadence <= 0) return { availability: 0, performance: 0, quality: 0, oee: 0, rejects: 0 };

    const operatingTime = plannedTime - downtime;
    const availability = (operatingTime / plannedTime) * 100;
    
    const theoreticalProd = (operatingTime / 60) * cadence;
    const performance = theoreticalProd > 0 ? (totalProd / theoreticalProd) * 100 : 0;
    
    const quality = totalProd > 0 ? (conformingProd / totalProd) * 100 : 0;
    
    const oee = (availability * performance * quality) / 10000;
    const rejects = totalProd - conformingProd;

    return {
      availability: Math.min(100, Math.max(0, availability)).toFixed(1),
      performance: Math.min(150, Math.max(0, performance)).toFixed(1),
      quality: Math.min(100, Math.max(0, quality)).toFixed(1),
      oee: Math.min(100, Math.max(0, oee)).toFixed(1),
      rejects: Math.round(Math.max(0, rejects) * 100) / 100
    };
  };

  // Check for preselected data from QR scan
  const preselectedData = location.state;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    // Handle preselected data from QR scan
    if (preselectedData && !loading) {
      if (preselectedData.preselectedSite && preselectedData.preselectedZone) {
        setSelectedSite(preselectedData.preselectedSite);
        setSelectedZone(preselectedData.preselectedZone);
        
        if (preselectedData.preselectedChecklist) {
          // If checklist is preselected, go directly to audit
          handleChecklistSelect(preselectedData.preselectedChecklist);
          toast.success('Tournée démarrée via QR code');
        } else {
          // Go to checklist selection
          setStep(2);
          fetchChecklists(preselectedData.preselectedZone.id);
          toast.success('Zone identifiée via QR code');
        }
        // Clear the location state
        window.history.replaceState({}, document.title);
      }
    }
  }, [preselectedData, loading]);

  const fetchInitialData = async () => {
    try {
      const [sitesRes, zonesRes, shiftsRes, trsObjRes, tourRankRes] = await Promise.all([
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/zones`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/work-shifts`, { headers: getAuthHeaders() }).catch(() => ({ data: { shifts: ['Jour', 'Soir', 'Nuit'] } })),
        axios.get(`${API}/settings/trs-objective`, { headers: getAuthHeaders() }).catch(() => ({ data: { trs_objective_global: 85 } })),
        axios.get(`${API}/tours/available-ranks`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);
      axios.get(`${API}/integrations/prodtrack/status`, { headers: getAuthHeaders() })
        .then((r) => setProdtrackStatus(r.data || { enabled: false, zones: [] }))
        .catch(() => setProdtrackStatus({ enabled: false, zones: [] }));
      setSites(sitesRes.data);
      setZones(zonesRes.data);
      if (shiftsRes.data?.shifts || shiftsRes.data?.work_shifts) {
        setWorkShifts(shiftsRes.data.shifts || shiftsRes.data.work_shifts);
      }
      if (trsObjRes.data?.trs_objective_global) {
        setTrsObjectiveGlobal(trsObjRes.data.trs_objective_global);
      }
      // Set tour rank info
      if (tourRankRes.data) {
        setTourRankInfo(tourRankRes.data);
        setSelectedTourRank(tourRankRes.data.suggested_rank);
        // Set default shift from enabled shifts
        if (tourRankRes.data.shifts_enabled?.length > 0) {
          setSelectedShift(tourRankRes.data.shifts_enabled[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklists = async (zoneId) => {
    try {
      const res = await axios.get(`${API}/checklists`, {
        params: { zone_id: zoneId, active_only: true, lang: i18n.language || 'fr' },
        headers: getAuthHeaders()
      });
      setChecklists(res.data);
      if (res.data.length === 0) {
        const tmplRes = await axios.get(`${API}/checklists/templates`, {
          params: { lang: i18n.language || 'fr' },
          headers: getAuthHeaders()
        });
        setTemplates(tmplRes.data);
      }
    } catch (error) {
      console.error('Error fetching checklists:', error);
    }
  };

  const handleSiteSelect = (site) => {
    setSelectedSite(site);
    setSelectedZone(null);
    setSelectedChecklist(null);
    setCompletedZones([]);
    setStep(1);
  };

  const handleZoneSelect = async (zone) => {
    setSelectedZone(zone);
    setSelectedChecklist(null);
    setTemplates([]);
    await fetchChecklists(zone.id);
    setStep(2);
  };

  const handleDeployTemplate = async (template) => {
    if (!selectedZone) return;
    setDeployingTemplate(true);
    try {
      const res = await axios.post(`${API}/checklists/deploy-template`, {
        template_id: template.id,
        zone_id: selectedZone.id,
        new_name: template.name
      }, { headers: getAuthHeaders() });
      toast.success(`Modèle "${template.name}" déployé sur ${selectedZone.name}`);
      await fetchChecklists(selectedZone.id);
    } catch (error) {
      console.error('Error deploying template:', error);
      toast.error('Erreur lors du déploiement du modèle');
    } finally {
      setDeployingTemplate(false);
    }
  };

  const handleChecklistSelect = (checklist) => {
    setSelectedChecklist(checklist);
    // Initialize responses
    const initialResponses = checklist.items.map(item => ({
      item_id: item.id,
      status: null,
      comment: '',
      photo_url: null
    }));
    setResponses(initialResponses);
    setCurrentItemIndex(0);
    setStep(3);
  };

  const handleStatusSelect = (status) => {
    const newResponses = [...responses];
    newResponses[currentItemIndex].status = status;
    setResponses(newResponses);
  };

  const handleCommentChange = (comment) => {
    const newResponses = [...responses];
    newResponses[currentItemIndex].comment = comment;
    setResponses(newResponses);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    toast.info('Traitement de l\'image...');
    
    try {
      // Compress image for faster upload on tablets
      const compressedFile = await compressImage(file, 1200, 0.8);
      
      const formData = new FormData();
      formData.append('file', compressedFile);

      const res = await axios.post(`${API}/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (percent < 100) {
            // Could show progress here if needed
          }
        }
      });

      const newResponses = [...responses];
      newResponses[currentItemIndex].photo_url = res.data.url;
      setResponses(newResponses);
      setCurrentPhoto(res.data.url);
      toast.success('Photo capturée avec succès');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error("Erreur lors de l'upload de la photo");
    } finally {
      setUploadingPhoto(false);
      // Reset file input for next capture
      if (e.target) e.target.value = '';
    }
  };

  // Compress image to reduce upload size (important for tablet cameras)
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            },
            'image/jpeg',
            quality
          );
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = () => {
    const newResponses = [...responses];
    newResponses[currentItemIndex].photo_url = null;
    setResponses(newResponses);
    setCurrentPhoto(null);
  };

  const goToNextItem = () => {
    if (currentItemIndex < selectedChecklist.items.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
      setCurrentPhoto(responses[currentItemIndex + 1]?.photo_url || null);
    } else {
      // Zone audit complete - go to zone summary
      setStep(4);
    }
  };

  const goToPreviousItem = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
      setCurrentPhoto(responses[currentItemIndex - 1]?.photo_url || null);
    }
  };

  // Complete current zone and move to next zone selection
  const completeCurrentZone = () => {
    // Validate all items have a status
    const incompleteItems = responses.filter(r => r.status === null);
    if (incompleteItems.length > 0) {
      toast.error(`${incompleteItems.length} item(s) sans statut`);
      return;
    }

    // Check photo requirements
    const missingPhotos = selectedChecklist.items.filter((item, idx) => 
      item.photo_required && responses[idx].status === 'NOK' && !responses[idx].photo_url
    );
    if (missingPhotos.length > 0) {
      toast.error(t('tour.photoRequiredForNok', { count: missingPhotos.length }));
      return;
    }

    // Validate TRS data if machine zone (obligatoire sauf si non planifié)
    let trsDataToSave = null;
    if (isMachineZone(selectedZone)) {
      if (isNotPlanned) {
        // Machine non planifiée - pas de TRS requis
        trsDataToSave = { not_planned: true };
      } else {
        // Machine planifiée - TRS obligatoire
        if (!trsData.theoretical_cadence || !trsData.planned_time || !trsData.total_production || !trsData.conforming_production) {
          toast.error('Les données TRS sont obligatoires pour les machines planifiées. Cochez "Non planifié" si la machine n\'a pas tourné.');
          return;
        }
        // mêmes règles que le serveur : une saisie incohérente serait refusée à la clôture
        const controle = calculerTRS(trsData);
        if (controle.erreur) {
          toast.error(controle.erreur);
          return;
        }
        trsDataToSave = {
          ...trsData,
          ...calculateTrs(),
          prodtrack: traceProdtrack()
        };
      }
    }

    // Save current zone data
    const zoneData = {
      zone: selectedZone,
      checklist: selectedChecklist,
      responses: [...responses],
      trsData: trsDataToSave
    };
    
    setCompletedZones([...completedZones, zoneData]);
    
    // Reset TRS data and not planned for next zone
    setTrsData({
      theoretical_cadence: '',
      planned_time: '',
      downtime: '',
      total_production: '',
      conforming_production: '',
      shift: ''
    });
    setIsNotPlanned(false);
    setProdtrack(null);
    
    // Check if there are more zones to audit
    const remainingZones = getRemainingZones();
    if (remainingZones.length > 1) {
      // More zones available - go to zone selection
      setStep(5);
      toast.success(`Zone "${selectedZone.name}" terminée ! Choisissez la zone suivante.`);
    } else {
      // All zones completed - go to final summary
      setStep(6);
      toast.success('Toutes les zones ont été auditées !');
    }
  };

  // Get zones that haven't been completed yet
  const getRemainingZones = () => {
    const completedZoneIds = completedZones.map(cz => cz.zone.id);
    if (selectedZone) {
      completedZoneIds.push(selectedZone.id);
    }
    return filteredZones.filter(z => !completedZoneIds.includes(z.id));
  };

  // Select next zone to audit
  const selectNextZone = async (zone) => {
    setSelectedZone(zone);
    setSelectedChecklist(null);
    setResponses([]);
    setCurrentItemIndex(0);
    await fetchChecklists(zone.id);
    setStep(2);
  };

  // Finish tour completely (submit all zones)
  const handleSubmitAllZones = async () => {
    setSubmitting(true);
    try {
      // Submit all completed zones
      const allZonesData = [...completedZones];
      
      // Add current zone if we're at final summary from zone summary
      if (selectedZone && selectedChecklist && responses.length > 0) {
        const hasCurrentZone = completedZones.some(cz => cz.zone.id === selectedZone.id);
        if (!hasCurrentZone) {
          // Check if we have TRS data for current zone
          let currentTrsData = null;
          if (isMachineZone(selectedZone) && trsData.planned_time && trsData.total_production) {
            currentTrsData = {
              ...trsData,
              ...calculateTrs(),
              prodtrack: traceProdtrack()
            };
          }
          allZonesData.push({
            zone: selectedZone,
            checklist: selectedChecklist,
            responses: responses,
            trsData: currentTrsData
          });
        }
      }

      const today = new Date().toISOString().split('T')[0];
      const createdTourIds = [];

      // Submit each zone as a separate tour
      for (const zoneData of allZonesData) {
        // Submit tour with shift and tour_rank
        const tourResponse = await axios.post(`${API}/tours`, {
          site_id: selectedSite.id,
          zone_id: zoneData.zone.id,
          checklist_id: zoneData.checklist.id,
          responses: zoneData.responses,
          shift: selectedShift,
          tour_rank: selectedTourRank
        }, { headers: getAuthHeaders() });
        
        createdTourIds.push(tourResponse.data.id);

        // Submit TRS data if available (skip if not_planned)
        if (zoneData.trsData && !zoneData.trsData.not_planned && zoneData.trsData.planned_time && zoneData.trsData.theoretical_cadence) {
          try {
            await axios.post(`${API}/trs-entries`, {
              zone_id: zoneData.zone.id,
              date: today,
              shift: selectedShift || zoneData.trsData.shift || null,  // Use tour's selected shift
              planned_time: parseFloat(zoneData.trsData.planned_time),
              downtime: parseFloat(zoneData.trsData.downtime) || 0,
              total_production: parseFloat(zoneData.trsData.total_production),
              conforming_production: parseFloat(zoneData.trsData.conforming_production) || parseFloat(zoneData.trsData.total_production),
              theoretical_cadence: parseFloat(zoneData.trsData.theoretical_cadence),
              ...(zoneData.trsData.prodtrack || {})
            }, { headers: getAuthHeaders() });
          } catch (trsError) {
            console.error('Error submitting TRS data:', trsError);
            // La tournée continue, mais l'utilisateur doit savoir que le TRS n'est pas enregistré
            toast.error(`TRS non enregistré pour ${zoneData.zone.name} : ${trsError.response?.data?.detail || 'erreur serveur'}`);
          }
        }
      }

      // Close all tours (lock them, calculate metrics, notify manager)
      let closedCount = 0;
      let needsAnalysisCount = 0;
      for (const tourId of createdTourIds) {
        try {
          const closeResponse = await axios.post(`${API}/tours/${tourId}/close`, {}, { headers: getAuthHeaders() });
          closedCount++;
          if (closeResponse.data.needs_analysis) {
            needsAnalysisCount++;
          }
        } catch (closeError) {
          console.error('Error closing tour:', closeError);
        }
      }

      const trsCount = allZonesData.filter(z => z.trsData?.planned_time).length;
      let successMsg = `Tournée clôturée avec succès ! ${allZonesData.length} zone(s) auditée(s).`;
      if (trsCount > 0) {
        successMsg += ` ${trsCount} entrée(s) TRS.`;
      }
      if (needsAnalysisCount > 0) {
        successMsg += ` ⚠️ ${needsAnalysisCount} zone(s) nécessitent une analyse.`;
      }
      toast.success(successMsg);
      
      // Store tour IDs and stats for success page
      setCreatedTourIds(createdTourIds);
      setTourSummaryStats({
        zonesCount: allZonesData.length,
        trsCount,
        needsAnalysisCount,
        stats: getAllZonesStats()
      });
      
      // Show success page with export options
      setStep(7);
    } catch (error) {
      console.error('Error submitting tours:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  // Get total stats across all completed zones
  const getAllZonesStats = () => {
    let ok = 0, nok = 0, na = 0;
    
    // Count from completed zones
    completedZones.forEach(zoneData => {
      zoneData.responses.forEach(r => {
        if (r.status === 'OK') ok++;
        else if (r.status === 'NOK') nok++;
        else if (r.status === 'NA') na++;
      });
    });
    
    // Count from current zone if applicable
    if (responses.length > 0) {
      responses.forEach(r => {
        if (r.status === 'OK') ok++;
        else if (r.status === 'NOK') nok++;
        else if (r.status === 'NA') na++;
      });
    }
    
    return { ok, nok, na, total: ok + nok + na };
  };

  const currentItem = selectedChecklist?.items?.[currentItemIndex];
  const currentResponse = responses[currentItemIndex];
  const itemsLength = selectedChecklist?.items?.length || 0;
  const progress = itemsLength > 0 ? ((currentItemIndex + 1) / itemsLength) * 100 : 0;

  const getStatusCounts = () => {
    const ok = responses.filter(r => r.status === 'OK').length;
    const nok = responses.filter(r => r.status === 'NOK').length;
    const na = responses.filter(r => r.status === 'NA').length;
    return { ok, nok, na };
  };

  const getCriticalityColor = (crit) => {
    switch (crit) {
      case 'haute': return 'text-red-600 bg-red-100';
      case 'moyenne': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const filteredZones = zones.filter(z => z.site_id === selectedSite?.id);
  const remainingZones = getRemainingZones();
  const totalZones = filteredZones.length;
  const completedCount = completedZones.length + (step >= 4 ? 1 : 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="gemba-walk">
      {/* Progress Header */}
      <div className="sticky top-0 z-20 bg-white border-b shadow-sm">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-bold font-['Barlow_Condensed'] text-slate-900">TOURNÉE GEMBA</h1>
            <div className="flex items-center gap-2">
              {selectedSite && totalZones > 0 && (
                <Badge variant="outline" className="text-xs">
                  {completedCount} / {totalZones} zones
                </Badge>
              )}
            </div>
          </div>
          {step === 3 && selectedChecklist && (
            <div>
              <div className="flex items-center justify-between text-sm text-slate-500 mb-1">
                <span>{currentItemIndex + 1} / {itemsLength}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}
          {/* Multi-zone progress */}
          {selectedSite && completedZones.length > 0 && step < 6 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {completedZones.map((cz, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs bg-green-100 text-green-700">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {cz.zone.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 pb-24">
        {/* Step 0: Select Site */}
        {step === 0 && (
          <div className="space-y-4 fade-in">
            {/* Tour info header */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Tournée du</p>
                    <p className="text-lg font-semibold text-slate-900 capitalize">{getTodayDate()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Superviseur</p>
                    <p className="text-lg font-semibold text-slate-900">{user?.name || user?.email || 'Non identifié'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tour Rank and Shift Selection */}
            {tourRankInfo && (
              <Card className="border-2 border-blue-300 bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600" />
                    Informations de la tournée
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Progress indicator */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <p className="text-sm text-slate-600">Tournées aujourd'hui</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {tourRankInfo.completed_tours} / {tourRankInfo.required_tours}
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      tourRankInfo.is_compliant 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {tourRankInfo.is_compliant ? '✓ Objectif atteint' : `${tourRankInfo.tours_remaining} restante(s)`}
                    </div>
                  </div>

                  {/* Alert if max tours reached */}
                  {!tourRankInfo.can_add_tour && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Limite de tournées atteinte</p>
                        <p className="text-xs text-amber-700">
                          Vous avez atteint la limite de {tourRankInfo.max_tours_per_day} tournées par jour.
                          {!tourRankInfo.allow_unlimited_tours && " Contactez votre administrateur si nécessaire."}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    {/* Tour Rank Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Rang de la tournée</Label>
                      <Select 
                        value={String(selectedTourRank)} 
                        onValueChange={(v) => setSelectedTourRank(parseInt(v))}
                        disabled={!tourRankInfo.can_add_tour}
                      >
                        <SelectTrigger data-testid="tour-rank-select">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {tourRankInfo.ranks?.map(rank => (
                            <SelectItem 
                              key={rank.rank} 
                              value={String(rank.rank)}
                              disabled={rank.is_done || !rank.is_available}
                            >
                              {rank.label} {rank.is_done ? '✓' : ''}
                            </SelectItem>
                          ))}
                          {/* Allow extra tours beyond required only if can_add_tour is true */}
                          {tourRankInfo.can_add_tour && tourRankInfo.completed_tours >= tourRankInfo.required_tours && (
                            <SelectItem value={String(tourRankInfo.completed_tours + 1)}>
                              Tournée {tourRankInfo.completed_tours + 1} (supplémentaire)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Shift Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Quart de travail</Label>
                      <Select 
                        value={selectedShift} 
                        onValueChange={setSelectedShift}
                        disabled={!tourRankInfo.can_add_tour}
                      >
                        <SelectTrigger data-testid="shift-select">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {(tourRankInfo.shifts_enabled || ['Jour', 'Soir', 'Nuit']).map(shift => (
                            <SelectItem key={shift} value={shift}>
                              {shift}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="text-center py-4">
              <Factory className="w-12 h-12 text-blue-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900">CHOISIR UN SITE</h2>
              <p className="text-slate-500 mt-1">Sélectionnez le site à inspecter</p>
            </div>

            {sites.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                  <p className="text-slate-500">{t('gemba.noSites')}</p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate('/sites')}>
                    {t('gemba.createSite')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {sites.map((site) => (
                  <Card
                    key={site.id}
                    className="cursor-pointer hover:border-blue-500 transition-colors active:scale-[0.98]"
                    onClick={() => handleSiteSelect(site)}
                    data-testid={`select-site-${site.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Factory className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{site.name}</h3>
                        {site.description && (
                          <p className="text-sm text-slate-500">{site.description}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {zones.filter(z => z.site_id === site.id).length} zone(s)
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 1: Select Zone */}
        {step === 1 && (
          <div className="space-y-4 fade-in">
            <Button variant="ghost" onClick={() => setStep(0)} className="mb-2">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>

            <div className="text-center py-4">
              <MapPin className="w-12 h-12 text-blue-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900">CHOISIR UNE ZONE</h2>
              <p className="text-slate-500 mt-1">{selectedSite?.name}</p>
              <p className="text-xs text-blue-600 mt-2">
                Vous devrez auditer toutes les zones avant de terminer la tournée
              </p>
            </div>

            {filteredZones.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                  <p className="text-slate-500">Aucune zone dans ce site</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {filteredZones.map((zone) => (
                  <Card
                    key={zone.id}
                    className="cursor-pointer hover:border-blue-500 transition-colors active:scale-[0.98]"
                    onClick={() => handleZoneSelect(zone)}
                    data-testid={`select-zone-${zone.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{zone.name}</h3>
                        <Badge variant="outline" className="mt-1">{zone.type}</Badge>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Checklist */}
        {step === 2 && (
          <div className="space-y-4 fade-in">
            <Button variant="ghost" onClick={() => setStep(completedZones.length > 0 ? 5 : 1)} className="mb-2">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>

            <div className="text-center py-4">
              <ClipboardList className="w-12 h-12 text-blue-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900">CHOISIR UNE CHECKLIST</h2>
              <p className="text-slate-500 mt-1">{selectedZone?.name}</p>
            </div>

            {checklists.length === 0 ? (
              <div className="space-y-4">
                <Card className="border-dashed">
                  <CardContent className="py-6 text-center">
                    <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-2" />
                    <p className="text-slate-500">{t('gemba.noChecklistsForZone')}</p>
                    <Button variant="outline" className="mt-3" onClick={() => navigate('/checklists')}>
                      {t('gemba.createChecklist')}
                    </Button>
                  </CardContent>
                </Card>

                {templates.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <Copy className="w-4 h-4 text-purple-600" />
                      <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">Déployer un modèle sur cette zone</h3>
                    </div>
                    <div className="grid gap-2">
                      {templates.filter(t => t.items?.length > 0).map((template) => (
                        <Card
                          key={template.id}
                          className="cursor-pointer hover:border-purple-400 transition-colors active:scale-[0.98]"
                          onClick={() => !deployingTemplate && handleDeployTemplate(template)}
                          data-testid={`deploy-template-${template.id}`}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                                <ClipboardList className="w-5 h-5 text-purple-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-slate-900 text-sm">{template.name}</h4>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <Badge variant="outline" className="text-xs border-purple-300 text-purple-700">{template.items?.length} points</Badge>
                                  {template.checklist_type && <Badge variant="outline" className="text-xs">{template.checklist_type}</Badge>}
                                </div>
                              </div>
                              {deployingTemplate ? (
                                <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid gap-3">
                {checklists.map((checklist) => {
                  const hasItems = (checklist.items?.length || 0) > 0;
                  return (
                    <Card
                      key={checklist.id}
                      className={`cursor-pointer transition-colors active:scale-[0.98] ${
                        hasItems 
                          ? 'hover:border-blue-500' 
                          : 'border-amber-300 bg-amber-50/50'
                      }`}
                      onClick={() => handleChecklistSelect(checklist)}
                      data-testid={`select-checklist-${checklist.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            hasItems ? 'bg-purple-100' : 'bg-amber-100'
                          }`}>
                            {hasItems ? (
                              <ClipboardList className="w-6 h-6 text-purple-600" />
                            ) : (
                              <AlertTriangle className="w-6 h-6 text-amber-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">{checklist.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge 
                                variant="outline" 
                                className={!hasItems ? 'border-amber-500 text-amber-700 bg-amber-50' : ''}
                              >
                                {checklist.items?.length || 0} items
                                {!hasItems && ' (vide)'}
                              </Badge>
                              <Badge variant="outline">{checklist.frequency === 'quotidien' ? 'Quotidien' : 'Hebdo'}</Badge>
                            </div>
                            {!hasItems && (
                              <p className="text-xs text-amber-600 mt-1">
                                Ajoutez des points de contrôle pour utiliser cette checklist
                              </p>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Audit Items */}
        {step === 3 && itemsLength === 0 && (
          <div className="space-y-4 fade-in">
            <Card>
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Checklist vide</h3>
                <p className="text-slate-500 mb-6">
                  Cette checklist n'a pas encore de points de contrôle à inspecter.
                  <br />
                  Veuillez d'abord ajouter des items dans la section Checklists.
                </p>
                <div className="flex justify-center gap-3">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Choisir une autre checklist
                  </Button>
                  <Button onClick={() => navigate('/checklists')} className="bg-[#0E346E] hover:bg-[#0A2850]">
                    Modifier la checklist
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {step === 3 && currentItem && (
          <div className="space-y-4 fade-in">
            {/* Current zone indicator */}
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 p-2 rounded-lg">
              <MapPin className="w-4 h-4" />
              <span className="font-medium">{selectedZone?.name}</span>
              <span className="text-slate-400">•</span>
              <span>{selectedChecklist?.name}</span>
            </div>

            {/* Item Info */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900">{currentItem.title}</h3>
                    {currentItem.description && (
                      <p className="text-sm text-slate-500 mt-1">{currentItem.description}</p>
                    )}
                    {currentItem.standard_expected && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-medium">Standard attendu</p>
                        <p className="text-sm text-slate-700 mt-1">{currentItem.standard_expected}</p>
                      </div>
                    )}
                  </div>
                  <Badge className={getCriticalityColor(currentItem.criticality)}>
                    {currentItem.criticality?.toUpperCase()}
                  </Badge>
                </div>

                {/* Photo requirement indicator */}
                {currentItem.photo_required && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-orange-600">
                    <Camera className="w-4 h-4" />
                    <span>{t('tour.photoRequiredIfNok')}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <Button
                className={`h-20 text-lg font-bold flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                  currentResponse?.status === 'OK'
                    ? 'bg-green-600 hover:bg-green-700 text-white ring-4 ring-green-300'
                    : 'bg-green-100 hover:bg-green-200 text-green-700'
                }`}
                onClick={() => handleStatusSelect('OK')}
                data-testid="status-ok-btn"
              >
                <CheckCircle2 className="w-8 h-8" />
                <span>{t('tour.conforming')}</span>
              </Button>
              <Button
                className={`h-20 text-lg font-bold flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                  currentResponse?.status === 'NOK'
                    ? 'bg-red-600 hover:bg-red-700 text-white ring-4 ring-red-300'
                    : 'bg-red-100 hover:bg-red-200 text-red-700'
                }`}
                onClick={() => handleStatusSelect('NOK')}
                data-testid="status-nok-btn"
              >
                <XCircle className="w-8 h-8" />
                <span>{t('tour.nonConforming')}</span>
              </Button>
              <Button
                className={`h-20 text-lg font-bold flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                  currentResponse?.status === 'NA'
                    ? 'bg-slate-600 hover:bg-slate-700 text-white ring-4 ring-slate-300'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
                onClick={() => handleStatusSelect('NA')}
                data-testid="status-na-btn"
              >
                <MinusCircle className="w-8 h-8" />
                <span>{t('tour.na')}</span>
              </Button>
            </div>

            {/* Comment & Photo (if non-conforming) */}
            {currentResponse?.status === 'NOK' && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">
                      Commentaire (optionnel)
                    </label>
                    <Textarea
                      value={currentResponse?.comment || ''}
                      onChange={(e) => handleCommentChange(e.target.value)}
                      placeholder="Décrivez l'écart constaté..."
                      rows={3}
                      data-testid="nok-comment"
                    />
                  </div>

                  {/* Photo Upload - Optimisé pour tablette */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">
                      Photo {currentItem.photo_required && currentResponse?.status === 'NOK' ? (
                        <span className="text-red-500">* (obligatoire)</span>
                      ) : '(optionnel)'}
                    </label>
                    {currentResponse?.photo_url ? (
                      <div className="relative">
                        <img
                          src={`${BACKEND_URL}${currentResponse.photo_url}`}
                          alt={t('tour.photoNonConforming')}
                          className="w-full h-48 object-cover rounded-lg border-2 border-green-500"
                        />
                        <div className="absolute top-2 right-2 flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-white"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Camera className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={removePhoto}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                          Photo capturée
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Input caché pour la caméra */}
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          ref={fileInputRef}
                          onChange={handlePhotoUpload}
                          className="hidden"
                          id="camera-input"
                        />
                        
                        {/* Boutons grands pour tablette tactile */}
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            variant="outline"
                            className="h-20 border-2 border-dashed hover:border-blue-500 hover:bg-blue-50 active:scale-95 transition-all"
                            onClick={() => {
                              // Force camera capture
                              const input = fileInputRef.current;
                              if (input) {
                                input.setAttribute('capture', 'environment');
                                input.click();
                              }
                            }}
                            disabled={uploadingPhoto}
                            data-testid="capture-photo-btn"
                          >
                            {uploadingPhoto ? (
                              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                            ) : (
                              <div className="flex flex-col items-center gap-1">
                                <Camera className="w-8 h-8 text-blue-600" />
                                <span className="text-xs font-medium text-blue-600">Appareil photo</span>
                              </div>
                            )}
                          </Button>
                          
                          <Button
                            variant="outline"
                            className="h-20 border-2 border-dashed hover:border-purple-500 hover:bg-purple-50 active:scale-95 transition-all"
                            onClick={() => {
                              // Open gallery without capture attribute
                              const input = fileInputRef.current;
                              if (input) {
                                input.removeAttribute('capture');
                                input.click();
                              }
                            }}
                            disabled={uploadingPhoto}
                            data-testid="gallery-photo-btn"
                          >
                            <div className="flex flex-col items-center gap-1">
                              <ImageIcon className="w-8 h-8 text-purple-600" />
                              <span className="text-xs font-medium text-purple-600">Galerie</span>
                            </div>
                          </Button>
                        </div>
                        
                        {/* Message d'aide */}
                        {currentItem.photo_required && currentResponse?.status === 'NOK' && (
                          <p className="text-xs text-red-500 text-center mt-2">
                            Une photo est requise pour les non-conformités sur ce point
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex items-center justify-between md:relative md:border-0 md:bg-transparent md:p-0 md:mt-6">
              <Button
                variant="outline"
                onClick={goToPreviousItem}
                disabled={currentItemIndex === 0}
                className="h-12"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
              <Button
                onClick={goToNextItem}
                disabled={!currentResponse?.status}
                className="h-12 bg-[#0E346E] hover:bg-[#0A2850]"
                data-testid="next-item-btn"
              >
                {currentItemIndex === itemsLength - 1 ? 'Terminer zone' : 'Suivant'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Zone Summary */}
        {step === 4 && (
          <div className="space-y-4 fade-in pb-24">
            <Button variant="ghost" onClick={() => { setStep(3); setCurrentItemIndex(Math.max(0, itemsLength - 1)); }} className="mb-2">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>

            <div className="text-center py-4">
              <h2 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900">RÉSUMÉ ZONE: {selectedZone?.name}</h2>
              <p className="text-slate-500 mt-1">{selectedChecklist?.name}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{getStatusCounts().ok}</p>
                  <p className="text-sm text-green-600">{t('tour.conforming')}</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 text-center">
                  <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{getStatusCounts().nok}</p>
                  <p className="text-sm text-red-600">{t('tour.nonConforming')}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="p-4 text-center">
                  <MinusCircle className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-700">{getStatusCounts().na}</p>
                  <p className="text-sm text-slate-600">{t('tour.na')}</p>
                </CardContent>
              </Card>
            </div>

            {/* Non-conforming items will create actions */}
            {getStatusCounts().nok > 0 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-orange-700 mb-3">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">{getStatusCounts().nok} action(s) sera(ont) créée(s)</span>
                  </div>
                  <div className="space-y-2">
                    {selectedChecklist?.items.map((item, idx) => {
                      if (responses[idx]?.status !== 'NOK') return null;
                      return (
                        <div key={item.id} className="flex items-center gap-2 text-sm p-2 bg-white rounded">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span className="text-slate-700">{item.title}</span>
                          <Badge className={getCriticalityColor(item.criticality)} variant="outline">
                            {item.criticality}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Items List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Détail des items</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {selectedChecklist?.items.map((item, idx) => (
                    <div
                      key={item.id}
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                      onClick={() => { setCurrentItemIndex(idx); setStep(3); }}
                    >
                      <div className="flex items-center gap-3">
                        {responses[idx]?.status === 'OK' && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                        {responses[idx]?.status === 'NOK' && <XCircle className="w-5 h-5 text-red-600" />}
                        {responses[idx]?.status === 'NA' && <MinusCircle className="w-5 h-5 text-slate-500" />}
                        {!responses[idx]?.status && <div className="w-5 h-5 rounded-full border-2 border-slate-300" />}
                        <span className="text-slate-700">{item.title}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* TRS Section - Only for machine/production zones */}
            {isMachineZone(selectedZone) && (
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-purple-600" />
                    Données TRS (OEE) - {selectedZone?.name}
                  </CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="border-purple-400 text-purple-700 bg-purple-50">
                      Objectif TRS: {selectedZone?.trs_objective || trsObjectiveGlobal}%
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Pilote ProdTrack : données de production récupérées automatiquement */}
                  {zoneReliee(selectedZone) && prodtrack && (
                    <div className={`p-3 rounded-lg border text-sm ${prodtrack.available ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
                         data-testid="prodtrack-banner">
                      <div className="flex items-center gap-2 font-medium">
                        <Plug className="w-4 h-4" />
                        {prodtrack.loading ? 'Récupération des données ProdTrack…'
                          : prodtrack.available ? 'Prérempli depuis ProdTrack' : 'ProdTrack : saisie manuelle'}
                        {!prodtrack.loading && (
                          <Button type="button" variant="ghost" size="sm" className="ml-auto h-7"
                                  onClick={() => chargerProdtrack(selectedZone)} data-testid="prodtrack-reload">
                            <RefreshCw className="w-3 h-3 mr-1" /> Recharger
                          </Button>
                        )}
                      </div>
                      {prodtrack.available && prodtrack.source && (
                        <div className="mt-1 text-emerald-900 space-y-1">
                          <p>
                            Ligne <b>{prodtrack.source.ligne?.nom}</b> · quart <b>{prodtrack.source.quart?.nom}</b>
                            {prodtrack.source.ordres_fabrication?.length > 0 && <> · OF {prodtrack.source.ordres_fabrication.map((o) => o.numero).join(', ')}</>}
                            {prodtrack.unit && <> · unité <b>{prodtrack.unit}</b></>}
                          </p>
                          <p>
                            TRS ProdTrack : <b>{prodtrack.source.trs_prodtrack?.trs ?? '—'}%</b>
                            {' '}(D {prodtrack.source.trs_prodtrack?.disponibilite ?? '—'}% · P {prodtrack.source.trs_prodtrack?.performance ?? '—'}% · Q {prodtrack.source.trs_prodtrack?.qualite ?? '—'}%)
                          </p>
                          <p className="text-xs text-emerald-700">Vérifiez et corrigez si besoin : vos modifications sont conservées et tracées.</p>
                        </div>
                      )}
                      {!prodtrack.loading && !prodtrack.available && prodtrack.reason && (
                        <p className="mt-1 text-slate-600">{prodtrack.reason}</p>
                      )}
                      {prodtrack.warnings?.map((w, i) => <p key={i} className="mt-1 text-amber-700">⚠ {w}</p>)}
                    </div>
                  )}

                  {/* Non planifié checkbox */}
                  <div className="flex items-center space-x-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <input
                      type="checkbox"
                      id="not-planned"
                      checked={isNotPlanned}
                      onChange={(e) => setIsNotPlanned(e.target.checked)}
                      className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                      data-testid="trs-not-planned"
                    />
                    <label htmlFor="not-planned" className="text-sm font-medium text-amber-800 cursor-pointer">
                      Machine non planifiée (pas de production prévue)
                    </label>
                  </div>

                  {/* TRS fields - disabled if not planned */}
                  <div className={isNotPlanned ? 'opacity-50 pointer-events-none' : ''}>
                    {/* Shift - Auto-filled from tour selection */}
                    <div className="space-y-2 mb-4">
                      <Label className="text-sm font-medium">Quart de travail</Label>
                      <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">{selectedShift || 'Non défini'}</span>
                        <span className="text-xs text-blue-600 ml-auto">(Défini au début de la tournée)</span>
                      </div>
                    </div>

                    {/* Cadence théorique - Saisie par le superviseur */}
                    <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-purple-500" />
                      Cadence théorique ({prodtrack?.available && prodtrack.unit && prodtrack.unit !== 'mixte' ? `${prodtrack.unit}/heure` : (selectedZone?.cadence_unit || 'unités/heure')}) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="ex: 120"
                      value={trsData.theoretical_cadence}
                      onChange={(e) => setTrsData({ ...trsData, theoretical_cadence: e.target.value })}
                      className="bg-white"
                      data-testid="trs-cadence"
                      disabled={isNotPlanned}
                    />
                  </div>

                  {/* Time inputs */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-1">
                        <Clock className="w-4 h-4 text-blue-500" />
                        Temps planifié (min) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="ex: 480"
                        value={trsData.planned_time}
                        onChange={(e) => setTrsData({ ...trsData, planned_time: e.target.value })}
                        className="bg-white"
                        data-testid="trs-planned-time"
                        disabled={isNotPlanned}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        Temps d'arrêt (min)
                      </Label>
                      <Input
                        type="number"
                        placeholder="ex: 30"
                        value={trsData.downtime}
                        onChange={(e) => setTrsData({ ...trsData, downtime: e.target.value })}
                        className="bg-white"
                        data-testid="trs-downtime"
                        disabled={isNotPlanned}
                      />
                    </div>
                  </div>

                  {/* Production inputs */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-1">
                        <Package className="w-4 h-4 text-purple-500" />
                        Production totale <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="ex: 1000"
                        value={trsData.total_production}
                        onChange={(e) => setTrsData({ ...trsData, total_production: e.target.value })}
                        className="bg-white"
                        data-testid="trs-total-production"
                        disabled={isNotPlanned}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        Production conforme <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="ex: 980"
                        value={trsData.conforming_production}
                        onChange={(e) => setTrsData({ ...trsData, conforming_production: e.target.value })}
                        className="bg-white"
                        data-testid="trs-conforming-production"
                        disabled={isNotPlanned}
                      />
                    </div>
                  </div>

                  {/* Calculated TRS values */}
                  {!isNotPlanned && (trsData.theoretical_cadence && trsData.planned_time && trsData.total_production) && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Calculs automatiques
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="p-2 bg-blue-100 rounded-lg text-center">
                          <p className="text-xs text-blue-600 font-medium">Disponibilité</p>
                          <p className="text-lg font-bold text-blue-700">{calculateTrs().availability}%</p>
                        </div>
                        <div className="p-2 bg-purple-100 rounded-lg text-center">
                          <p className="text-xs text-purple-600 font-medium">Performance</p>
                          <p className="text-lg font-bold text-purple-700">{calculateTrs().performance}%</p>
                        </div>
                        <div className="p-2 bg-green-100 rounded-lg text-center">
                          <p className="text-xs text-green-600 font-medium">Qualité</p>
                          <p className="text-lg font-bold text-green-700">{calculateTrs().quality}%</p>
                        </div>
                        <div className={`p-2 rounded-lg text-center ${
                          selectedZone?.trs_objective 
                            ? (parseFloat(calculateTrs().oee) >= selectedZone.trs_objective ? 'bg-green-100' : 'bg-red-100')
                            : (parseFloat(calculateTrs().oee) >= 85 ? 'bg-green-100' : parseFloat(calculateTrs().oee) >= 60 ? 'bg-yellow-100' : 'bg-red-100')
                        }`}>
                          <p className={`text-xs font-medium ${
                            parseFloat(calculateTrs().oee) >= 85 ? 'text-green-600' : 
                            parseFloat(calculateTrs().oee) >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>TRS</p>
                          <p className={`text-lg font-bold ${
                            parseFloat(calculateTrs().oee) >= 85 ? 'text-green-700' : 
                            parseFloat(calculateTrs().oee) >= 60 ? 'text-yellow-700' : 'text-red-700'
                          }`}>{calculateTrs().oee}%</p>
                        </div>
                      </div>
                      <div className="mt-2 text-center">
                        <Badge variant="outline" className="text-red-600 border-red-300">
                          Rebuts : {calculateTrs().rejects.toLocaleString('fr-FR')} {prodtrack?.available && prodtrack.unit && prodtrack.unit !== 'mixte' ? prodtrack.unit : 'unités'}
                        </Badge>
                      </div>
                    </div>
                  )}
                  </div>
                  {/* End of isNotPlanned wrapper */}
                  
                  {/* Show message when not planned */}
                  {isNotPlanned && (
                    <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-amber-800 font-medium">Machine non planifiée</p>
                      <p className="text-sm text-amber-600 mt-1">Aucune donnée TRS ne sera enregistrée pour cette zone.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Next Zone or Finish Button */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 md:relative md:border-0 md:bg-transparent md:p-0">
              <Button
                className="w-full h-14 text-lg bg-[#0E346E] hover:bg-[#0A2850] font-bold uppercase tracking-wide"
                onClick={completeCurrentZone}
                disabled={submitting}
                data-testid="complete-zone-btn"
              >
                {remainingZones.length > 1 ? (
                  <>
                    <MapPin className="w-5 h-5 mr-2" />
                    Zone suivante ({remainingZones.length - 1} restante{remainingZones.length > 2 ? 's' : ''})
                  </>
                ) : (
                  <>
                    <Flag className="w-5 h-5 mr-2" />
                    Terminer la tournée
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Select Next Zone */}
        {step === 5 && (
          <div className="space-y-4 fade-in">
            <div className="text-center py-4">
              <MapPin className="w-12 h-12 text-blue-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900">CHOISIR LA ZONE SUIVANTE</h2>
              <p className="text-slate-500 mt-1">{selectedSite?.name}</p>
              <Badge variant="outline" className="mt-2">
                {completedZones.length} zone(s) terminée(s) • {remainingZones.length} restante(s)
              </Badge>
            </div>

            {/* Completed zones summary */}
            {completedZones.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Zones terminées
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {completedZones.map((cz, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-green-100 text-green-700">
                        {cz.zone.name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Remaining zones */}
            <div className="grid gap-3">
              {remainingZones.map((zone) => (
                <Card
                  key={zone.id}
                  className="cursor-pointer hover:border-blue-500 transition-colors active:scale-[0.98]"
                  onClick={() => selectNextZone(zone)}
                  data-testid={`select-next-zone-${zone.id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{zone.name}</h3>
                      <Badge variant="outline" className="mt-1">{zone.type}</Badge>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Option to finish early */}
            {remainingZones.length > 0 && (
              <div className="mt-6 pt-4 border-t">
                <p className="text-sm text-slate-500 text-center mb-3">
                  Vous pouvez terminer maintenant avec {completedZones.length} zone(s) auditée(s)
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setStep(6)}
                  data-testid="finish-early-btn"
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Terminer sans les zones restantes
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 6: Final Summary (All Zones) */}
        {step === 6 && (
          <div className="space-y-4 fade-in pb-24">
            <div className="text-center py-4">
              <Flag className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900">RÉSUMÉ FINAL DE LA TOURNÉE</h2>
              <p className="text-slate-500 mt-1">{selectedSite?.name}</p>
            </div>

            {/* Overall Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{getAllZonesStats().ok}</p>
                  <p className="text-sm text-green-600">{t('tour.conforming')}</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 text-center">
                  <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{getAllZonesStats().nok}</p>
                  <p className="text-sm text-red-600">{t('tour.nonConforming')}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="p-4 text-center">
                  <MinusCircle className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-700">{getAllZonesStats().na}</p>
                  <p className="text-sm text-slate-600">{t('tour.na')}</p>
                </CardContent>
              </Card>
            </div>

            {/* Total items */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Total items inspectés</span>
                  <span className="text-2xl font-bold text-slate-900">{getAllZonesStats().total}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-slate-600">Zones auditées</span>
                  <span className="text-2xl font-bold text-slate-900">
                    {completedZones.length + (responses.length > 0 ? 1 : 0)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Zones breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Détail par zone</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {completedZones.map((zoneData, idx) => {
                    const zoneOk = zoneData.responses.filter(r => r.status === 'OK').length;
                    const zoneNok = zoneData.responses.filter(r => r.status === 'NOK').length;
                    return (
                      <div key={idx} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            <span className="font-semibold text-slate-900">{zoneData.zone.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-green-50 text-green-700">{zoneOk} OK</Badge>
                            {zoneNok > 0 && (
                              <Badge variant="outline" className="bg-red-50 text-red-700">{zoneNok} NOK</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{zoneData.checklist.name}</p>
                      </div>
                    );
                  })}
                  {/* Current zone if not yet saved */}
                  {responses.length > 0 && !completedZones.some(cz => cz.zone.id === selectedZone?.id) && (
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-slate-900">{selectedZone?.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-green-50 text-green-700">{getStatusCounts().ok} OK</Badge>
                          {getStatusCounts().nok > 0 && (
                            <Badge variant="outline" className="bg-red-50 text-red-700">{getStatusCounts().nok} NOK</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{selectedChecklist?.name}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Warning if some zones not audited */}
            {remainingZones.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-amber-700 mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">{remainingZones.length} zone(s) non auditée(s)</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {remainingZones.map((zone) => (
                      <Badge key={zone.id} variant="outline" className="border-amber-400 text-amber-700">
                        {zone.name}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setStep(5)}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Continuer l'audit
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Actions to be created */}
            {getAllZonesStats().nok > 0 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-orange-700 mb-3">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">{getAllZonesStats().nok} action(s) sera(ont) créée(s)</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Submit Button */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 md:relative md:border-0 md:bg-transparent md:p-0">
              <Button
                className="w-full h-14 text-lg bg-[#0E346E] hover:bg-[#0A2850] font-bold uppercase tracking-wide"
                onClick={handleSubmitAllZones}
                disabled={submitting || (completedZones.length === 0 && responses.length === 0)}
                data-testid="submit-tour-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Flag className="w-5 h-5 mr-2" />
                    Valider la tournée complète
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 7: Success Page with Export Options */}
        {step === 7 && (
          <div className="space-y-6 fade-in p-4">
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-900">
                TOURNÉE TERMINÉE
              </h2>
              <p className="text-slate-500 mt-2">
                {selectedSite?.name} • {tourSummaryStats?.zonesCount || 0} zone(s) auditée(s)
              </p>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-green-700">{tourSummaryStats?.stats?.ok || 0}</p>
                  <p className="text-sm text-green-600">{t('tour.conforming')}</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 text-center">
                  <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-red-700">{tourSummaryStats?.stats?.nok || 0}</p>
                  <p className="text-sm text-red-600">{t('tour.nonConforming')}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="p-4 text-center">
                  <MinusCircle className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-700">{tourSummaryStats?.stats?.na || 0}</p>
                  <p className="text-sm text-slate-600">{t('tour.na')}</p>
                </CardContent>
              </Card>
            </div>

            {/* Export Section */}
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Flag className="w-5 h-5 text-blue-600" />
                  Télécharger le récapitulatif
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Générez un document PDF complet avec toutes les zones de votre tournée et les photos de non-conformités.
                </p>
                
                {/* Main Complete Export Button */}
                {createdTourIds.length > 0 && (
                  <div className="space-y-4">
                    <CompleteTourExport 
                      tourIds={createdTourIds}
                      language={i18n.language || 'fr'}
                    />
                    
                    {/* Optional: Individual zone exports (collapsed) */}
                    {createdTourIds.length > 1 && (
                      <details className="mt-4">
                        <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700">
                          Exporter par zone individuellement ({createdTourIds.length} zones)
                        </summary>
                        <div className="space-y-2 mt-3">
                          {createdTourIds.map((tourId, index) => (
                            <div key={tourId} className="flex items-center justify-between bg-white p-2 rounded-lg border">
                              <span className="text-sm text-slate-600">
                                Zone {index + 1}
                              </span>
                              <TourSummaryExport 
                                tourId={tourId} 
                                language={i18n.language || 'fr'}
                              />
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Warning if needs analysis */}
            {tourSummaryStats?.needsAnalysisCount > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">
                      {tourSummaryStats.needsAnalysisCount} zone(s) nécessitent une analyse par le manager
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Return to Dashboard */}
            <Button
              className="w-full h-14 text-lg bg-[#0E346E] hover:bg-[#0A2850] font-bold uppercase tracking-wide"
              onClick={() => navigate('/')}
              data-testid="return-dashboard-btn"
            >
              Retour au tableau de bord
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GembaWalk;
